import { FC } from 'react';

import { Heading } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { SingleSelectComboBox } from '@telialt/common-select-combo-box/src';
import { useAddLeasingPlanMutation } from '@telialt/core-eshop/src/hooks/mutations/useAddLeasingPlanMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useIsRatePlanMutating } from '@telialt/core-eshop/src/hooks/useIsRatePlanMutating';
import { Loader } from '@telialt/core-eshop/src/product/Loader';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { InitialPaymentOption } from '@telialt/models/src/EshopModel';
import {
    selectIsFullPricePayment,
    selectLeasingPlanCode,
    selectPossibleLeasingPlans,
} from '@telialt/utils/src/eshopSelectors';
import { withParams } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import { RestrictionMessageNotification } from '../RestrictionMessageNotification';
import { useIsLeasingPlanMutating } from '../useIsLeasingPlanMutating';
import { useLeasingRestriction } from '../useLeasingRestriction';
import { PaymentSummary } from './PaymentSummary';
import { PaymentTypeSelector } from './PaymentTypeSelector';
import { PredefinedLeasingSelectorWrapper, SelectTitle } from './PredefinedLeasingSelector.styles';
import { useSelectInitialPaymentOptionMutation } from './useSelectInitialPaymentOptionMutation';

export const PredefinedLeasingSelector: FC = () => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const possibleLeasingPlans = selectPossibleLeasingPlans(cart);
    const isFullPricePayment = selectIsFullPricePayment(cart);
    const restriction = useLeasingRestriction();

    const { addLeasingPlan } = useAddLeasingPlanMutation();
    const { isLoadingSelectInitialPaymentOption, selectInitialPaymentOption } = useSelectInitialPaymentOptionMutation();
    const isLoadingLeasingPlans = useIsLeasingPlanMutating();
    const isLoadingDependencies = useIsRatePlanMutating();
    const isLoading = isLoadingLeasingPlans || isLoadingDependencies || isLoadingSelectInitialPaymentOption;

    const paymentTypeSelectorId = useComponentId('payment-type-selector');
    const paymentPeriodSelectorId = useComponentId('payment-period-selector');
    const paymentInitialPaymentSelectorId = useComponentId('payment-initial-payment-selector');

    if (!possibleLeasingPlans?.length) {
        return null;
    }

    if (isFullPricePayment || restriction) {
        return (
            <PredefinedLeasingSelectorWrapper data-test={ESHOP_TEST_ID.LEASING_PLAN_SELECTOR}>
                <Loader isLoading={isLoading} dataTest={ESHOP_TEST_ID.LEASING_PLAN_LOADER} />
                <Heading variant="title-100" tag="h3" id={paymentTypeSelectorId}>
                    {t['eshop.product_page.leasing_selector.title']}
                </Heading>
                <PaymentTypeSelector ariaLabelledBy={paymentTypeSelectorId} />
                <RestrictionMessageNotification />
                <PaymentSummary />
            </PredefinedLeasingSelectorWrapper>
        );
    }

    const selectedLeasingPlanCode = selectLeasingPlanCode(cart);
    const selectedInitialPaymentOption = cart?.selectedInitialPaymentOption;
    const initialPaymentOptions = cart?.initialPaymentOptions || [];

    const handleLeasingPlanChange = <T extends { code: string }>({ code }: T) => addLeasingPlan(code);
    const handleInitialPaymentChange = <T extends InitialPaymentOption>(selected: T) =>
        selectInitialPaymentOption(selected);

    const leasingPeriodsComboBoxOptions = possibleLeasingPlans.map((leasingPlan) => ({
        ...leasingPlan,
        id: leasingPlan.code,
        label: withParams(t['eshop.product_page.leasing_selector.period_selector.option_text'], {
            period: leasingPlan.durationInMonths,
        }),
    }));
    const initialPaymentComboBoxOptions = initialPaymentOptions.map((option) => ({
        ...option,
        id: option.title,
        label: (
            <span>
                {option.initialPaymentPrice.formattedValue}&ensp;-&ensp;{option.title}
            </span>
        ),
    }));

    return (
        <PredefinedLeasingSelectorWrapper data-test={ESHOP_TEST_ID.LEASING_PLAN_SELECTOR}>
            <Loader isLoading={isLoading} dataTest={ESHOP_TEST_ID.LEASING_PLAN_LOADER} />
            <Heading variant="title-100" tag="h3" id={paymentTypeSelectorId}>
                {t['eshop.product_page.leasing_selector.title']}
            </Heading>
            <PaymentTypeSelector ariaLabelledBy={paymentTypeSelectorId} />

            <SelectTitle variant="subsection-100" tag="h4" id={paymentPeriodSelectorId}>
                {t['eshop.product_page.leasing_selector.period_selector.title']}
            </SelectTitle>
            <SingleSelectComboBox
                initialOptionId={selectedLeasingPlanCode}
                options={leasingPeriodsComboBoxOptions}
                onSelect={handleLeasingPlanChange}
                dataTest={ESHOP_TEST_ID.LEASING_PERIOD_SELECTOR}
                ariaLabelledBy={paymentPeriodSelectorId}
            />

            {initialPaymentComboBoxOptions?.length > 1 && (
                <>
                    <SelectTitle variant="subsection-100" tag="h4" id={paymentInitialPaymentSelectorId}>
                        {t['eshop.product_page.leasing_selector.predefined_initial_payment_selector.title']}
                    </SelectTitle>
                    <SingleSelectComboBox
                        initialOptionId={selectedInitialPaymentOption?.title}
                        options={initialPaymentComboBoxOptions}
                        onSelect={handleInitialPaymentChange}
                        dataTest={ESHOP_TEST_ID.PREDEFINED_INITIAL_PAYMENT_SELECTOR}
                        ariaLabelledBy={paymentInitialPaymentSelectorId}
                    />
                </>
            )}

            <PaymentSummary />
        </PredefinedLeasingSelectorWrapper>
    );
};
